import React, { useState, useEffect } from 'react';
import { websiteAnalyticsService } from '../../services/websiteAnalyticsService';
import { TrafficPattern, WebsiteAnalyticsFilters } from '../../types/websiteAnalytics';
import LineChart from '../Charts/LineChart';
import BarChart from '../Charts/BarChart';
import MetricCard from '../Common/MetricCard';

interface TrafficPatternAnalyticsProps {
  filters?: WebsiteAnalyticsFilters;
  refreshInterval?: number;
}

export const TrafficPatternAnalytics: React.FC<TrafficPatternAnalyticsProps> = ({
  filters,
  refreshInterval = 300000
}) => {
  const [trafficPattern, setTrafficPattern] = useState<TrafficPattern | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedView, setSelectedView] = useState<'hourly' | 'daily' | 'peaks' | 'seasonal'>('hourly');

  const fetchTrafficPatterns = async () => {
    try {
      setLoading(true);
      const response = await websiteAnalyticsService.getTrafficPatterns(filters);
      
      if (response.success && response.data) {
        setTrafficPattern(response.data);
        setError(null);
      } else {
        setError('Failed to fetch traffic pattern data');
      }
    } catch (err) {
      setError('Error loading traffic pattern analytics');
      console.error('Error fetching traffic patterns:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrafficPatterns();
    
    const interval = setInterval(fetchTrafficPatterns, refreshInterval);
    return () => clearInterval(interval);
  }, [filters, refreshInterval]);

  if (loading && !trafficPattern) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchTrafficPatterns}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!trafficPattern) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No traffic pattern data available</p>
      </div>
    );
  }

  const formatNumber = (value: number): string => {
    return websiteAnalyticsService.formatNumber(value);
  };

  const formatDuration = (seconds: number): string => {
    return websiteAnalyticsService.formatDuration(seconds);
  };

  // Calculate summary metrics - with safety checks
  const hourlyData = trafficPattern?.hourlyTraffic || [];
  const dailyData = trafficPattern?.dailyTraffic || [];
  
  const totalSessions = hourlyData.reduce((sum, hour) => sum + (hour.sessions || 0), 0);
  const totalPageViews = hourlyData.reduce((sum, hour) => sum + (hour.pageViews || 0), 0);
  const avgSessionDuration = hourlyData.length > 0 
    ? hourlyData.reduce((sum, hour) => sum + (hour.averageDuration || 0), 0) / hourlyData.length 
    : 0;
  
  const peakHour = hourlyData.length > 0 
    ? hourlyData.reduce((max, hour) => 
        (hour.sessions || 0) > (max.sessions || 0) ? hour : max
      )
    : { hour: 0, sessions: 0, pageViews: 0, averageDuration: 0 };

  const peakDay = dailyData.length > 0
    ? dailyData.reduce((max, day) => 
        (day.sessions || 0) > (max.sessions || 0) ? day : max
      )
    : { date: new Date().toISOString(), sessions: 0, uniqueUsers: 0 };

  // Prepare chart data based on selected view for Recharts
  const getChartData = () => {
    switch (selectedView) {
      case 'hourly':
        return hourlyData.map(hour => ({
          name: `${hour.hour || 0}:00`,
          sessions: hour.sessions || 0,
          pageViews: hour.pageViews || 0
        }));

      case 'daily':
        return dailyData.map(day => ({
          name: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          sessions: day.sessions || 0,
          uniqueUsers: day.uniqueUsers || 0
        }));

      case 'peaks':
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const peakHours = trafficPattern?.peakHours || [];
        return peakHours.map(peak => ({
          name: `${dayNames[peak.dayOfWeek || 0]} ${peak.hour || 0}:00`,
          peakSessions: peak.peakSessions || 0
        }));

      case 'seasonal':
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const seasonalTrends = trafficPattern?.seasonalTrends || [];
        return seasonalTrends.map(trend => ({
          name: `${monthNames[(trend.month || 1) - 1]} ${trend.year || new Date().getFullYear()}`,
          sessions: trend.sessions || 0,
          growth: trend.growth || 0
        }));

      default:
        return [];
    }
  };



  const getDayName = (dayOfWeek: number): string => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  const getTimeIcon = (hour: number): string => {
    if (hour >= 6 && hour < 12) return '🌅';
    if (hour >= 12 && hour < 18) return '☀️';
    if (hour >= 18 && hour < 22) return '🌆';
    return '🌙';
  };

  return (
    <div className="space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Sessions"
          value={formatNumber(totalSessions)}
          icon={<span>👥</span>}
          color="blue"
        />
        <MetricCard
          title="Total Page Views"
          value={formatNumber(totalPageViews)}
          icon={<span>👁️</span>}
          color="green"
        />
        <MetricCard
          title="Avg Session Duration"
          value={formatDuration(avgSessionDuration)}
          icon={<span>⏱️</span>}
          color={avgSessionDuration > 180 ? 'green' : 'yellow'}
        />
        <MetricCard
          title="Peak Hour"
          value={`${peakHour.hour}:00 (${formatNumber(peakHour.sessions)} sessions)`}
          icon={<span>{getTimeIcon(peakHour.hour)}</span>}
          color="purple"
        />
      </div>

      {/* View Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          {[
            { key: 'hourly', label: 'Hourly Traffic', icon: '🕐' },
            { key: 'daily', label: 'Daily Trends', icon: '📅' },
            { key: 'peaks', label: 'Peak Hours', icon: '📈' },
            { key: 'seasonal', label: 'Seasonal Trends', icon: '📊' }
          ].map((view) => (
            <button
              key={view.key}
              onClick={() => setSelectedView(view.key as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium ${
                selectedView === view.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <span>{view.icon}</span>
              <span>{view.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Main Chart */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">
          {selectedView === 'hourly' && 'Hourly Traffic Pattern'}
          {selectedView === 'daily' && 'Daily Traffic Trends'}
          {selectedView === 'peaks' && 'Peak Traffic Hours'}
          {selectedView === 'seasonal' && 'Seasonal Traffic Trends'}
        </h3>
        <div className="relative" style={{ minHeight: '350px' }}>
          {selectedView === 'peaks' ? (
            <BarChart 
              data={getChartData()} 
              xAxisKey="name"
              bars={[{
                key: 'peakSessions',
                color: '#EF4444',
                name: 'Peak Sessions'
              }]}
              height={300}
            />
          ) : selectedView === 'hourly' ? (
            <LineChart 
              data={getChartData()} 
              xAxisKey="name"
              lines={[
                {
                  key: 'sessions',
                  color: '#3B82F6',
                  name: 'Sessions'
                },
                {
                  key: 'pageViews',
                  color: '#10B981',
                  name: 'Page Views'
                }
              ]}
              height={300}
            />
          ) : selectedView === 'daily' ? (
            <LineChart 
              data={getChartData()} 
              xAxisKey="name"
              lines={[
                {
                  key: 'sessions',
                  color: '#3B82F6',
                  name: 'Sessions'
                },
                {
                  key: 'uniqueUsers',
                  color: '#F59E0B',
                  name: 'Unique Users'
                }
              ]}
              height={300}
            />
          ) : (
            <LineChart 
              data={getChartData()} 
              xAxisKey="name"
              lines={[
                {
                  key: 'sessions',
                  color: '#8B5CF6',
                  name: 'Sessions'
                },
                {
                  key: 'growth',
                  color: '#10B981',
                  name: 'Growth %'
                }
              ]}
              height={300}
            />
          )}
        </div>
      </div>

      {/* Peak Hours Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Peak Traffic Hours</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Day
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Peak Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avg Sessions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(trafficPattern?.peakHours || [])
                  .sort((a, b) => (b.peakSessions || 0) - (a.peakSessions || 0))
                  .slice(0, 10)
                  .map((peak, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="mr-2">{getTimeIcon(peak.hour)}</span>
                          <span className="text-sm font-medium text-gray-900">
                            {peak.hour}:00
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getDayName(peak.dayOfWeek)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(peak.peakSessions)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(peak.averageSessions)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">Daily Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sessions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unique Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bounce Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dailyData
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .slice(0, 10)
                  .map((day, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {new Date(day.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(day.sessions)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(day.uniqueUsers)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {websiteAnalyticsService.formatPercentage(day.bounceRate)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Geographic Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Traffic Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl mb-2">🌅</div>
            <div className="text-sm font-medium text-gray-900">Morning Peak</div>
            <div className="text-2xl font-bold text-blue-600">
              {(trafficPattern?.peakHours || [])
                .filter(peak => (peak.hour || 0) >= 6 && (peak.hour || 0) < 12)
                .reduce((max, peak) => (peak.peakSessions || 0) > max ? (peak.peakSessions || 0) : max, 0)
              }
            </div>
            <div className="text-sm text-gray-500">6AM - 12PM</div>
          </div>

          <div className="text-center">
            <div className="text-3xl mb-2">☀️</div>
            <div className="text-sm font-medium text-gray-900">Afternoon Peak</div>
            <div className="text-2xl font-bold text-yellow-600">
              {(trafficPattern?.peakHours || [])
                .filter(peak => (peak.hour || 0) >= 12 && (peak.hour || 0) < 18)
                .reduce((max, peak) => (peak.peakSessions || 0) > max ? (peak.peakSessions || 0) : max, 0)
              }
            </div>
            <div className="text-sm text-gray-500">12PM - 6PM</div>
          </div>

          <div className="text-center">
            <div className="text-3xl mb-2">🌆</div>
            <div className="text-sm font-medium text-gray-900">Evening Peak</div>
            <div className="text-2xl font-bold text-purple-600">
              {(trafficPattern?.peakHours || [])
                .filter(peak => (peak.hour || 0) >= 18 && (peak.hour || 0) < 24)
                .reduce((max, peak) => (peak.peakSessions || 0) > max ? (peak.peakSessions || 0) : max, 0)
              }
            </div>
            <div className="text-sm text-gray-500">6PM - 12AM</div>
          </div>
        </div>
      </div>
    </div>
  );
};